import * as React from 'react';
import { styled } from '@glitz/react';
import { translate } from '@avensia/excite';
import { shadedGrey, tintedBlack } from 'webapp/shared/sharedTheme';

interface Props {
  rating: number;
  showRatingCount?: boolean;
  onClick?: () => void;
}

export default React.memo(
  styled(function RatingStars({ rating, showRatingCount = true, onClick }: Props) {
    const totalStars = 5;
    const ratingInPercent = rating / totalStars;
    return (
      <Rating
        onClick={onClick}
        role={onClick ? 'button' : 'img'}
        tabIndex={onClick ? 0 : undefined}
        aria-label={translate(t => t.productPage.ratingsAriaText, { rating, totalStars })}
      >
        {Array(totalStars)
          .fill(null)
          .map((_, i) => (
            <Star
              css={{
                ':before': {
                  width: `${Math.min(1, getStarWidthRatio(i, ratingInPercent, totalStars)) * 100}%`,
                },
              }}
              key={i}
            />
          ))}
        {showRatingCount && rating > 0 && <RatingCount>({rating})</RatingCount>}
      </Rating>
    );
  }),
);

const getStarWidthRatio = (index: number, rating: number, totalStars: number) => {
  const k = totalStars * rating;

  if (k <= 1 && index === 0) {
    return k;
  }

  return k - index;
};

const Rating = styled.div({
  display: 'flex',
  alignItems: 'center',
  columnGap: 3,
});

const Star = styled.span({
  width: 12,
  height: 12,
  position: 'relative',
  backgroundColor: t => t.ratings?.backgroundColor || shadedGrey,
  clipPath: 'polygon(50% 73%, 20% 90%, 28% 58%, 2.5% 35%, 37% 32%, 50% 0%, 63% 32%, 97% 35%, 72% 60%, 78% 90%)',
  ':before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    backgroundColor: t => t.ratings?.color || tintedBlack,
  },
});
const RatingCount = styled.span({
  fontSize: t => t.sharedFontSize.xxs,
  fontWeight: 500,
  margin: { left: 4 },
});
